/**
 * One-click sign-in for demo and local development.
 *
 * Gated on `VITE_DEMO_ACCOUNTS`, which is set in `.env` (local) and absent
 * from `.env.example` and any real deployment. That gate is the whole point:
 * a login page with working credentials printed on it is a login page with no
 * login, and "we'll remember to take it out before launch" is not a control.
 *
 * The flag is read through `import.meta.env`, so when it is unset Vite
 * replaces the check with `false` and the entire block below is dropped at
 * build time — the credentials are not merely hidden in production, they are
 * not in the bundle.
 */

export type DemoAccount = {
  label: string
  description: string
  email: string
  password: string
}

/**
 * One per trade, plus a cashier.
 *
 * The trades are here because the commonest question a prospect asks is "what
 * does this look like for MY kind of shop", and the honest answer used to be a
 * restaurant regardless of what they ran. Each of these is a real workspace
 * with its industry profile applied, so a chemist signing in sees Medicines in
 * the nav and a wholesaler sees prices quoted before tax.
 *
 * The CASHIER is here for the opposite reason, and is deliberately not an
 * owner. The most useful thing a demo can show is what somebody CANNOT do —
 * the create buttons that are absent, the margins that are not on screen — and
 * that is invisible if every button signs you in as the owner.
 */
const ACCOUNTS: DemoAccount[] = [
  {
    label: 'Restaurant',
    description: 'Menu items, tables, aggregator payouts, a month of orders',
    email: 'restaurant@voltra.test',
    password: 'demo-password',
  },
  {
    label: 'Grocery',
    description: 'Loose weight in kg and grams, fresh produce, fast billing',
    email: 'grocery@voltra.test',
    password: 'demo-password',
  },
  {
    label: 'Pharmacy',
    description: 'Calls its stock Medicines — strips, bottles, devices',
    email: 'pharmacy@voltra.test',
    password: 'demo-password',
  },
  {
    label: 'Salon',
    description: 'Sells Services that hold no stock, plus a retail shelf',
    email: 'salon@voltra.test',
    password: 'demo-password',
  },
  {
    label: 'Wholesale',
    description: 'Prices quoted before tax, sold by the case',
    email: 'wholesale@voltra.test',
    password: 'demo-password',
  },
  {
    label: 'Retail shop',
    description: 'The baseline — a good till and nothing exotic',
    email: 'retail@voltra.test',
    password: 'demo-password',
  },
  {
    label: 'Cashier',
    description: 'Can sell; no margins, no billing',
    email: 'cashier@voltra.test',
    password: 'demo-password',
  },
]

export const demoAccountsEnabled = import.meta.env.VITE_DEMO_ACCOUNTS === 'true'

export const demoAccounts: DemoAccount[] = demoAccountsEnabled ? ACCOUNTS : []

/**
 * The superadmin, which is NOT one of the buttons above.
 *
 * Platform admins live in their own table behind their own guard and cannot
 * hold a tenant token at all — that separation is what lets `users.tenant_id`
 * be NOT NULL with no exceptions, and it is why the only route from here into
 * a tenant is audited impersonation.
 *
 * So this is a LINK to the console's own sign-in, not a button that fills the
 * form. A button would post credentials this form can never accept — the two
 * principals authenticate against different tables — and the resulting
 * "invalid credentials" reads as a bug rather than as a boundary.
 */
export const superadmin = {
  label: 'Platform console',
  description: 'Separate sign-in — workspaces, plans, webhooks',
  email: 'admin@voltrapos.test',
  password: 'password',
  /*
   * A route in THIS app now, not a URL on the API host.
   *
   * The console used to be a server-rendered Filament panel at
   * {API}/platform. It is a React screen in the same bundle, so this is a
   * plain path — and it stays correct whatever VITE_API_URL is set to.
   */
  url: '/platform/login',
}

